import { createContext, useContext } from "react";

// =============================================================================
// FieldContext
//
// Set by the <Field> wrapper, consumed by <Input> / <Textarea> so they
// automatically pick up the right aria-describedby (linking to the error
// or hint paragraph below the input) and aria-invalid (when there's an
// error). Without this, screen readers don't announce the error message
// when the input is focused — they only see the standalone <p>.
// =============================================================================

export interface FieldContextValue {
  /** The field control's resolved id — the caller's `htmlFor`, or a generated
   *  fallback. A single-input control (e.g. AddressField) can adopt it as its
   *  own `id` so the <Label htmlFor> associates even when the call site didn't
   *  wire an explicit id. */
  fieldId?: string;
  errorId?: string;
  hintId?: string;
  invalid?: boolean;
}

export const FieldContext = createContext<FieldContextValue>({});

/** The surrounding <Field>'s resolved control id, or undefined when rendered
 *  outside a Field. Lets a single-input control adopt the id for label
 *  association without the call site repeating it. */
export function useFieldId(): string | undefined {
  return useContext(FieldContext).fieldId;
}

/** Pulls the describedBy id + invalid flag from the surrounding <Field>.
 *  Returns empty defaults when the consumer is rendered outside a Field
 *  so existing standalone usage keeps working. */
export function useFieldAria(): {
  describedBy: string | undefined;
  invalid: boolean | undefined;
} {
  const { errorId, hintId, invalid } = useContext(FieldContext);
  // Error always wins over hint — screen readers should hear the error
  // message rather than instructional text once a validation issue
  // surfaces.
  const describedBy = errorId ?? hintId;
  return { describedBy, invalid };
}
